/**
 * Data loading + mutations for the Team Settings → Video tab (FR-TENANT-05,
 * FR-VIDEO-03/04/05).
 *
 * The per-event embed list keeps its whole query (search, provider filter,
 * sort, direction, page) in the address bar, so a refresh, a bookmark and a
 * shared link all restore the same view — same idiom as `use-stat-fields.ts`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { ApiError } from '../../lib/api-error';
import type {
  EventVideoEmbed,
  EventVideoEmbedInput,
  EventVideoEmbedPage,
  EventVideoEmbedSortField,
  TeamEvent,
  VideoSettings,
  VideoSettingsPatch,
} from '../../lib/api-types';
import { enumParam, intParam, readUrlState, textParam, writeUrlState } from '../../lib/url-state';

const SORT_FIELDS: readonly EventVideoEmbedSortField[] = [
  'event_name',
  'scheduled_at',
  'updated_at',
];
const PAGE_SIZE = 10;
/** How many events the "configure an event" picker offers. */
const EVENT_PICKER_LIMIT = 50;

export interface EmbedQueryState {
  search: string;
  provider: 'hudl' | 'youtube' | 'all';
  sort: EventVideoEmbedSortField;
  direction: 'asc' | 'desc';
  page: number;
}

/**
 * Declarative screen state (shared url-state kit). Keys are `embed_`-prefixed
 * because the embeds table shares the team-settings URL with other sections.
 */
const EMBEDS_URL_STATE = {
  search: textParam('embed_q'),
  provider: enumParam('embed_provider', ['hudl', 'youtube', 'all'] as const, 'all'),
  sort: enumParam('embed_sort', SORT_FIELDS, 'scheduled_at'),
  direction: enumParam('embed_direction', ['asc', 'desc'] as const, 'desc'),
  page: intParam('embed_page', { fallback: 1, min: 1 }),
};

function readQuery(params: URLSearchParams): EmbedQueryState {
  return readUrlState(EMBEDS_URL_STATE, params);
}

export interface VideoSettingsController {
  settings: VideoSettings | null;
  isLoading: boolean;
  /** Human-readable load failure, never a stack trace. */
  error: string | null;
  /**
   * The structured failure behind `error`, so the view can tell a 402
   * `TIER_LIMIT_EXCEEDED` (render the upgrade CTA) from a 404
   * `ROUTE_NOT_FOUND` (a deployment/gateway fault, not a user problem) from
   * anything else (generic retry).
   */
  loadError: ApiError | null;
  reload: () => void;
  saveSettings: (patch: VideoSettingsPatch) => Promise<VideoSettings>;

  query: EmbedQueryState;
  setQuery: (patch: Partial<EmbedQueryState>) => void;
  embeds: readonly EventVideoEmbed[];
  embedMeta: EventVideoEmbedPage['meta'] | null;
  isEmbedsLoading: boolean;
  embedsError: string | null;
  saveEmbed: (eventId: string, input: EventVideoEmbedInput) => Promise<void>;
  removeEmbed: (eventId: string, provider: 'hudl' | 'youtube') => Promise<void>;

  /** Events available in the "add an event" picker. */
  events: readonly TeamEvent[];
}

function readError(caught: unknown, fallback: string): string {
  return ApiError.isApiError(caught) ? caught.message : fallback;
}

export function useVideoSettings(): VideoSettingsController {
  const apiClient = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(() => readQuery(searchParams), [searchParams]);

  const [settings, setSettings] = useState<VideoSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [embedPage, setEmbedPage] = useState<EventVideoEmbedPage | null>(null);
  const [isEmbedsLoading, setIsEmbedsLoading] = useState(true);
  const [embedsError, setEmbedsError] = useState<string | null>(null);
  const [events, setEvents] = useState<readonly TeamEvent[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    apiClient
      .getVideoSettings(controller.signal)
      .then((loaded) => {
        setSettings(loaded);
        setError(null);
        setLoadError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(readError(caught, 'Video settings could not be loaded.'));
        setLoadError(ApiError.isApiError(caught) ? caught : null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [apiClient, reloadToken]);

  useEffect(() => {
    const controller = new AbortController();
    setIsEmbedsLoading(true);
    apiClient
      .listEventVideoEmbeds(
        {
          q: query.search === '' ? undefined : query.search,
          provider: query.provider === 'all' ? undefined : query.provider,
          sort: query.sort,
          direction: query.direction,
          page: query.page,
          limit: PAGE_SIZE,
        },
        controller.signal
      )
      .then((page) => {
        setEmbedPage(page);
        setEmbedsError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setEmbedsError(readError(caught, 'The event video list could not be loaded.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsEmbedsLoading(false);
      });
    return () => controller.abort();
  }, [apiClient, query, reloadToken]);

  useEffect(() => {
    const controller = new AbortController();
    apiClient
      .listEvents(
        { limit: EVENT_PICKER_LIMIT, sort_by: 'scheduled_at', sort_order: 'desc' },
        controller.signal
      )
      .then((page) => setEvents(page.items))
      // The picker is a convenience: a failure here must not break the tab.
      .catch(() => setEvents([]));
    return () => controller.abort();
  }, [apiClient]);

  const setQuery = useCallback(
    (patch: Partial<EmbedQueryState>) => {
      const next = { ...query, ...patch };
      // Any filter change resets to page 1 unless the caller set the page.
      if (patch.page === undefined) next.page = 1;
      setSearchParams(writeUrlState(EMBEDS_URL_STATE, next, searchParams), { replace: true });
    },
    [query, searchParams, setSearchParams]
  );

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const saveSettings = useCallback(
    async (patch: VideoSettingsPatch): Promise<VideoSettings> => {
      const updated = await apiClient.updateVideoSettings(patch);
      setSettings(updated);
      return updated;
    },
    [apiClient]
  );

  const saveEmbed = useCallback(
    async (eventId: string, input: EventVideoEmbedInput): Promise<void> => {
      await apiClient.saveEventVideoEmbed(eventId, input);
      reload();
    },
    [apiClient, reload]
  );

  const removeEmbed = useCallback(
    async (eventId: string, provider: 'hudl' | 'youtube'): Promise<void> => {
      await apiClient.removeEventVideoEmbed(eventId, provider);
      reload();
    },
    [apiClient, reload]
  );

  return {
    settings,
    isLoading,
    error,
    loadError,
    reload,
    saveSettings,
    query,
    setQuery,
    embeds: embedPage?.embeds ?? [],
    embedMeta: embedPage?.meta ?? null,
    isEmbedsLoading,
    embedsError,
    saveEmbed,
    removeEmbed,
    events,
  };
}
